import { useState, useEffect, useCallback } from 'react';
import { partesService } from '../services/partesService';
import ConfirmDialog from './ConfirmDialog';
import { useConfirm } from '../hooks/useConfirm';

const VACIO = { cTarifa: '', paredes: '', techos: '', superficie: '', piso: '', ubicacion: '' };

export default function ParteTrabajoPanel({ idSiniestro, soloLectura = false }) {
  const [lista, setLista]           = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [form, setForm]             = useState(VACIO);
  const [editandoId, setEditandoId] = useState(null);
  const [guardando, setGuardando]   = useState(false);
  const [eliminando, setEliminando] = useState(null);
  const [error, setError]           = useState(null);
  const { confirm, pedirConfirm }   = useConfirm();

  const cargar = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await partesService.listar(idSiniestro);
      setLista(r.data);
    } catch {
      setError('Error al cargar partes de trabajo');
    } finally {
      setIsLoading(false);
    }
  }, [idSiniestro]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleChange = e =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleNuevo = () => { setEditandoId('nuevo'); setForm(VACIO); setError(null); };

  const handleEditar = (p) => {
    setEditandoId(p.id);
    setForm({
      cTarifa: p.cTarifa || '', paredes: p.paredes || '', techos: p.techos || '',
      superficie: p.superficie ?? '', piso: p.piso || '', ubicacion: p.ubicacion || '',
    });
    setError(null);
  };

  const handleCancelar = () => { setEditandoId(null); setForm(VACIO); };

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const payload = { ...form, superficie: form.superficie ? parseFloat(form.superficie) : null };
      if (editandoId === 'nuevo') {
        await partesService.crear(idSiniestro, payload);
      } else {
        await partesService.actualizar(idSiniestro, editandoId, payload);
      }
      setEditandoId(null);
      setForm(VACIO);
      await cargar();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Error al guardar';
      setError(msg);
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    const ok = await pedirConfirm({
      titulo: 'Eliminar zona de trabajo',
      mensaje: '¿Eliminar esta zona de trabajo? Esta acción no se puede deshacer.',
      peligro: true,
      textOk: 'Eliminar',
    });
    if (!ok) return;
    setEliminando(id);
    try {
      await partesService.eliminar(idSiniestro, id);
      await cargar();
    } catch {
      setError('Error al eliminar');
    } finally {
      setEliminando(null);
    }
  };

  const superficieTotal = lista.reduce((s, p) => s + (Number(p.superficie) || 0), 0);

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
          Parte de Trabajo
          {lista.length > 0 && (
            <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-normal normal-case">
              {lista.length} zonas
            </span>
          )}
        </h3>
        {editandoId === null && !soloLectura && (
          <button onClick={handleNuevo}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Añadir
          </button>
        )}
      </div>

      {error && <p role="alert" className="text-xs text-red-600">{error}</p>}

      {/* Formulario inline */}
      {editandoId !== null && !soloLectura && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Tarifa</label>
              <input name="cTarifa" value={form.cTarifa} onChange={handleChange}
                placeholder="Ej: 3105"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Superficie (m²)</label>
              <input name="superficie" type="number" step="0.01" value={form.superficie} onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Piso</label>
              <input name="piso" value={form.piso} onChange={handleChange}
                placeholder="Ej: 2º B"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Ubicación</label>
              <input name="ubicacion" value={form.ubicacion} onChange={handleChange}
                placeholder="Ej: BAÑO, SALON..."
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Paredes</label>
              <input name="paredes" value={form.paredes} onChange={handleChange}
                placeholder="X / XXXX"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Techos</label>
              <input name="techos" value={form.techos} onChange={handleChange}
                placeholder="X / XXXX"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={handleGuardar} disabled={guardando}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-1.5 rounded-lg transition disabled:opacity-50">
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
            <button onClick={handleCancelar}
              className="flex-1 border border-gray-300 text-gray-600 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-50 transition">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Tabla de zonas */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"/>
        </div>
      ) : lista.length === 0 && editandoId === null ? (
        <p className="text-xs text-gray-400 text-center py-3">Sin zonas de trabajo</p>
      ) : lista.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 border-b border-gray-100">
                <th className="text-left py-1.5 font-medium">Tarifa</th>
                <th className="text-left py-1.5 font-medium">Piso</th>
                <th className="text-left py-1.5 font-medium">Ubicación</th>
                <th className="text-center py-1.5 font-medium">Techos</th>
                <th className="text-center py-1.5 font-medium">Paredes</th>
                <th className="text-right py-1.5 font-medium">m²</th>
                {!soloLectura && <th className="py-1.5 w-12"/>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {lista.map(p => (
                <tr key={p.id} className="group hover:bg-gray-50">
                  <td className="py-1.5 text-gray-600">{p.cTarifa || '—'}</td>
                  <td className="py-1.5 text-gray-700">{p.piso || '—'}</td>
                  <td className="py-1.5 font-medium text-gray-900">{p.ubicacion}</td>
                  <td className="py-1.5 text-center text-gray-500">{p.techos || '—'}</td>
                  <td className="py-1.5 text-center text-gray-500">{p.paredes || '—'}</td>
                  <td className="py-1.5 text-right font-medium text-blue-700">
                    {p.superficie != null ? `${Number(p.superficie).toFixed(2)}` : '—'}
                  </td>
                  {!soloLectura && (
                  <td className="py-1.5">
                    <div className="flex gap-1">
                      <button onClick={() => handleEditar(p)} title="Editar"
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                        </svg>
                      </button>
                      <button onClick={() => handleEliminar(p.id)} title="Eliminar"
                        aria-label="Eliminar zona de trabajo"
                        disabled={eliminando === p.id}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-40">
                        {eliminando === p.id
                          ? <div className="w-3.5 h-3.5 animate-spin rounded-full border-2 border-red-400 border-t-transparent"/>
                          : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                        }
                      </button>
                    </div>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Total superficie */}
      {!isLoading && lista.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between items-center">
          <span className="text-sm font-semibold text-gray-600">Total superficie</span>
          <span className="text-lg font-bold text-blue-700">{superficieTotal.toFixed(2)} m²</span>
        </div>
      )}

      <ConfirmDialog {...confirm} abierto={confirm !== null} />
    </div>
  );
}
